import React, { useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import CryptoJS from "crypto-js";
import AdminNavbar from "../../layout/AdminNavbar";
import AdvisorNavbar from "../../layout/AdvisorNavbar";
import LocContext from "../../context/locContext";
import { useNavigate, useSearchParams, NavLink } from "react-router-dom";
import Swal from "sweetalert2";

const IMPERSONATION_ADMIN_TOKEN = "impersonation_admin_token";
const IMPERSONATION_ADMIN_USER = "impersonation_admin_user";

const TEAL = "#06b6a4";
const TEAL_LIGHT = "rgba(6, 182, 164, 0.12)";

function formatMobile(cc, phone) {
  if (!phone) return "—";
  const c = (cc || "").toString().trim();
  return c ? `${c}${phone}` : phone;
}

function formatAum(val) {
  if (val == null || val === "") return "0";
  const n = Number(val);
  return isNaN(n) ? "0" : n.toLocaleString("en-IN");
}

export default function ClientList() {
  const { userData } = useContext(LocContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("client_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [openActionId, setOpenActionId] = useState(null);
  const actionRef = useRef(null);

  const isAdmin = userData?.role_name === "Admin";
  const agentId = !isAdmin ? (userData?.id ?? userData?.user_id) : null;

  useEffect(() => {
    const page = searchParams.get("page") || 1;
    fetchClients(Number(page), search, sortBy, sortOrder);
  }, [agentId, sortBy, sortOrder]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (actionRef.current && !actionRef.current.contains(e.target)) {
        setOpenActionId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchClients = async (page = 1, searchTerm = "", sort = sortBy, order = sortOrder) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 20,
        search: searchTerm || search,
        sortBy: sort || sortBy,
        sortOrder: order || sortOrder,
      };
      if (agentId != null && agentId !== "") {
        params.agent_id = Number(agentId);
      }
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/clients`, { params });
      const raw = res.data;
      if (raw && raw.status === 1) {
        setClients(Array.isArray(raw.data) ? raw.data : []);
        const pag = raw.pagination || {};
        setPagination({
          total: pag.total ?? 0,
          page: pag.page ?? 1,
          limit: pag.limit ?? 20,
          totalPages: pag.totalPages ?? 1,
        });
      } else {
        setClients([]);
        setPagination({ total: 0, page: 1, limit: 20, totalPages: 0 });
      }
    } catch (err) {
      console.error("Error fetching clients:", err);
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || "Failed to load client list";
      Swal.fire({ icon: "error", title: "Error", text: msg, timer: 3000 });
      setClients([]);
      setPagination({ total: 0, page: 1, limit: 20, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    fetchClients(1, search, sortBy, sortOrder);
  };

  const handleSort = (col) => {
    const nextOrder = sortBy === col && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(col);
    setSortOrder(nextOrder);
    fetchClients(pagination.page, search, col, nextOrder);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === clients.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(clients.map((c) => c.id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleEditProfile = (client) => {
    setOpenActionId(null);
    navigate(`/agent/clients/profile?id=${client.id}`);
  };

  const handleGoToDashboard = (client) => {
    setOpenActionId(null);
    navigate(`/clientview/${client.id}`);
  };

  const handleGoToClientDashboard = async (client) => {
    setOpenActionId(null);
    if (!isAdmin) {
      handleGoToDashboard(client);
      return;
    }
    const adminToken = localStorage.getItem("authToken");
    if (!adminToken) {
      Swal.fire({ icon: "error", title: "Session expired", text: "Please log in again as Admin." });
      return;
    }
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/impersonate`,
        { clientId: client.id },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const data = res.data;
      if (!data || data.status !== 1 || !data.token || !data.clientUser) {
        Swal.fire({ icon: "error", title: "Error", text: data?.message || "Impersonation failed." });
        return;
      }
      const currentUserEncrypted = localStorage.getItem(process.env.REACT_APP_LOGIN_DATA_STORE);
      sessionStorage.setItem(IMPERSONATION_ADMIN_TOKEN, adminToken);
      sessionStorage.setItem(IMPERSONATION_ADMIN_USER, currentUserEncrypted || "");

      const clientUserEncrypted = CryptoJS.AES.encrypt(
        JSON.stringify(data.clientUser),
        process.env.REACT_APP_LOGIN_SEC
      ).toString();
      localStorage.setItem(process.env.REACT_APP_LOGIN_DATA_STORE, clientUserEncrypted);
      localStorage.setItem("authToken", data.token);

      window.location.href = "/overview";
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Impersonation failed.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    }
  };
  const handleSendInvitation = (client) => {
    setOpenActionId(null);
    Swal.fire({
      icon: "info",
      title: "Send Invitation",
      html: `Invitation will be sent to <strong>${client.email || "client"}</strong>. This can be wired to your backend later.`,
      timer: 2500,
    });
  };

  const Nav = isAdmin ? AdminNavbar : AdvisorNavbar;

  return (
    <>

      <div className="main-content" >
      <div className="page-wrapper page-settings">
      <div className="content">

        {/* Breadcrumb and Header Section with Card Shadow */}
        <div
          className="mb-4"
          style={{
            background: "#ffffff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            padding: "20px",
          }}
        >
          <div className="content-page-header content-page-headersplit">
            <h5>Client List</h5>
          </div>
          <nav
            aria-label="breadcrumb"
            style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}
          >
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <NavLink to="/dashboard">Dashboard</NavLink> /
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Client List
              </li>
            </ol>
          </nav>
        </div>


        <div className="card shadow-sm p-4 mb-4" style={{ borderRadius: "12px" }}>
          <form onSubmit={handleSearchSubmit} className="d-flex flex-wrap gap-2 align-items-center">
            <div className="position-relative flex-grow-1" style={{ minWidth: "200px" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#64748b" }} />
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, email, mobile, username..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: "36px", borderRadius: "8px", border: "2px solid #e2e8f0" }}
              />
            </div>
            <button type="submit" className="btn btn-primary" style={{ borderRadius: "8px", background: TEAL, borderColor: TEAL }}>
              Search
            </button>
            <span className="text-muted small">
              Total: {loading ? "..." : pagination.total} clients
            </span>
          </form>
        </div>

        <div className="card shadow-sm" style={{ borderRadius: "12px", overflow: "hidden" }}>
          {loading ? (
            <div className="p-5 text-center">
              <div className="spinner-border text-primary" role="status" />
              <p className="mt-2 mb-0 text-muted">Loading clients...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr style={{ background: TEAL, color: "#fff" }}>
                    <th style={{ padding: "14px 16px", border: "none", fontWeight: "600" }}>
                      <input
                        type="checkbox"
                        checked={clients.length > 0 && selectedIds.size === clients.length}
                        onChange={toggleSelectAll}
                        aria-label="Select all"
                      />
                    </th>
                    <th style={{ padding: "14px 16px", border: "none", fontWeight: "600" }}>
                      <button type="button" className="btn btn-link p-0 text-white text-decoration-none d-inline-flex align-items-center gap-1" onClick={() => handleSort("client_name")}>
                        Name <i className={`fa fa-sort-${sortBy === "client_name" ? (sortOrder === "asc" ? "up" : "down") : "arrows-v"}`} />
                      </button>
                      <i className="fa fa-search ms-1 opacity-75" style={{ fontSize: "0.85rem" }} title="Searchable" />
                    </th>
                    <th style={{ padding: "14px 16px", border: "none", fontWeight: "600" }}>
                      <button type="button" className="btn btn-link p-0 text-white text-decoration-none d-inline-flex align-items-center gap-1" onClick={() => handleSort("relationship_manager")}>
                        Relationship Manager <i className={`fa fa-sort-${sortBy === "relationship_manager" ? (sortOrder === "asc" ? "up" : "down") : "arrows-v"}`} />
                      </button>
                      <i className="fa fa-search ms-1 opacity-75" style={{ fontSize: "0.85rem" }} title="Searchable" />
                    </th>
                    <th style={{ padding: "14px 16px", border: "none", fontWeight: "600" }}>
                      <button type="button" className="btn btn-link p-0 text-white text-decoration-none d-inline-flex align-items-center gap-1" onClick={() => handleSort("sub_broker")}>
                        Sub Broker <i className={`fa fa-sort-${sortBy === "sub_broker" ? (sortOrder === "asc" ? "up" : "down") : "arrows-v"}`} />
                      </button>
                      <i className="fa fa-search ms-1 opacity-75" style={{ fontSize: "0.85rem" }} title="Searchable" />
                    </th>
                    <th style={{ padding: "14px 16px", border: "none", fontWeight: "600" }}>
                      <button type="button" className="btn btn-link p-0 text-white text-decoration-none d-inline-flex align-items-center gap-1" onClick={() => handleSort("aum")}>
                        AUM <i className={`fa fa-sort-${sortBy === "aum" ? (sortOrder === "asc" ? "up" : "down") : "arrows-v"}`} />
                      </button>
                    </th>
                    <th style={{ padding: "14px 16px", border: "none", fontWeight: "600" }}>
                      <button type="button" className="btn btn-link p-0 text-white text-decoration-none d-inline-flex align-items-center gap-1" onClick={() => handleSort("mobile")}>
                        Mobile <i className={`fa fa-sort-${sortBy === "mobile" ? (sortOrder === "asc" ? "up" : "down") : "arrows-v"}`} />
                      </button>
                      <i className="fa fa-search ms-1 opacity-75" style={{ fontSize: "0.85rem" }} title="Searchable" />
                    </th>
                    <th style={{ padding: "14px 16px", border: "none", fontWeight: "600" }}>
                      <button type="button" className="btn btn-link p-0 text-white text-decoration-none d-inline-flex align-items-center gap-1" onClick={() => handleSort("email")}>
                        Email <i className={`fa fa-sort-${sortBy === "email" ? (sortOrder === "asc" ? "up" : "down") : "arrows-v"}`} />
                      </button>
                      <i className="fa fa-search ms-1 opacity-75" style={{ fontSize: "0.85rem" }} title="Searchable" />
                    </th>
                    <th style={{ padding: "14px 16px", border: "none", fontWeight: "600" }}>
                      UserName <i className="fa fa-search ms-1 opacity-75" style={{ fontSize: "0.85rem" }} title="Searchable" />
                    </th>
                    <th style={{ padding: "14px 16px", border: "none", fontWeight: "600", width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client, idx) => (
                    <tr key={client.id} style={{ background: idx % 2 === 1 ? "#f8fafc" : "#fff" }}>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(client.id)}
                          onChange={() => toggleSelect(client.id)}
                          aria-label={`Select ${client.client_name}`}
                        />
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none"
                          style={{ color: TEAL, borderBottom: "1px dashed currentColor", fontWeight: "600" }}
                          onClick={() => handleGoToClientDashboard(client)}
                          title={isAdmin ? "Go to Client Dashboard" : "Go to Dashboard"}
                        >
                          {(client.client_name || "").trim() || "—"}
                        </button>
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle", color: "#64748b" }}>
                        {(client.relationship_manager || "").trim() || "—"}
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle", color: "#64748b" }}>
                        {(client.sub_broker || "").trim() || "—"}
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                        {formatAum(client.aum)}
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                        <a
                          href={`tel:${formatMobile(client.country_code, client.phone)}`}
                          style={{ color: TEAL, borderBottom: "1px dashed currentColor", textDecoration: "none" }}
                        >
                          {formatMobile(client.country_code, client.phone)}
                        </a>
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle" }}>
                        <a
                          href={`mailto:${client.email || ""}`}
                          style={{ color: TEAL, borderBottom: "1px dashed currentColor", textDecoration: "none" }}
                        >
                          {client.email || "—"}
                        </a>
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle", color: "#475569" }}>
                        {client.user_name || "—"}
                      </td>
                      <td style={{ padding: "14px 16px", verticalAlign: "middle", position: "relative" }} ref={openActionId === client.id ? actionRef : null}>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-dark"
                          aria-label="Actions"
                          onClick={() => setOpenActionId(openActionId === client.id ? null : client.id)}
                        >
                          <i className="fa fa-ellipsis-v" style={{ fontSize: "1.1rem" }} />
                        </button>
                        {openActionId === client.id && (
                          <div
                            className="dropdown-menu show shadow"
                            style={{
                              position: "absolute",
                              right: 0,
                              top: "100%",
                              zIndex: 1050,
                              minWidth: "180px",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <button type="button" className="dropdown-item py-2" onClick={() => handleEditProfile(client)}>
                              <i className="fa fa-pen me-2" style={{ color: "#64748b" }} /> Edit Profile
                            </button>
                            <button type="button" className="dropdown-item py-2" onClick={() => handleGoToClientDashboard(client)}>
                              <i className="fa fa-house me-2" style={{ color: "#64748b" }} /> {isAdmin ? "Go to Client Dashboard" : "Go to Dashboard"}
                            </button>
                            <button type="button" className="dropdown-item py-2" onClick={() => handleSendInvitation(client)}>
                              <i className="fa fa-paper-plane me-2" style={{ color: "#64748b" }} /> Send Invitation
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && clients.length === 0 && (
            <div className="p-5 text-center text-muted">
              No clients found. Try adjusting your search.
            </div>
          )}

          {!loading && pagination.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
              <small className="text-muted">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} clients)
              </small>
              <div className="d-flex gap-1">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchClients(pagination.page - 1, search, sortBy, sortOrder)}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchClients(pagination.page + 1, search, sortBy, sortOrder)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
        </div>                                              
      </div>
    </>
  );
}
